import { RouterProvider } from '@tanstack/react-router'
import { RefreshCwIcon } from 'lucide-react'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { registerDebugHandlers } from '@/lib/native-auth'
import { useSession } from '@/lib/session'
import { router } from './router'

function Splash() {
  return (
    <main
      data-view="splash"
      data-state="loading"
      aria-busy="true"
      className="flex min-h-svh items-center justify-center"
    >
      <Spinner className="size-6 text-muted-foreground" />
      <span className="sr-only">Loading hugo</span>
    </main>
  )
}

function Unreachable({ onRetry }: { onRetry: () => void }) {
  return (
    <main
      data-view="unreachable"
      data-state="error"
      className="flex min-h-svh flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <h1 className="font-heading text-xl font-semibold">hugo is unreachable</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        The server did not answer. Check your connection and try again.
      </p>
      <Button variant="outline" onClick={onRetry}>
        <RefreshCwIcon aria-hidden="true" />
        Try again
      </Button>
    </main>
  )
}

registerDebugHandlers({ navigate: (to) => void router.navigate({ to }) })

export function App() {
  const session = useSession()
  const signedIn = session.status === 'signed-in'

  useEffect(() => {
    if (session.status === 'loading' || session.status === 'error') return
    void router.invalidate()
  }, [session.status])

  if (session.status === 'loading') return <Splash />
  if (session.status === 'error') return <Unreachable onRetry={() => void session.refresh()} />
  return <RouterProvider router={router} context={{ auth: { signedIn } }} />
}
